import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { UserRpps } from '@saas/interfaces/core'
import React from 'react'
import { FieldErrors, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { getIdsAnnDirectoryPhysicianRppsRppsProfessionCodeProfession } from '@toktokdoctech/tokapi-client'
import { displaySnackbar } from '../../../actions/main'
import { makePointer } from '../../../api/_utils'
import { findPhysicianByRpps } from '../../../api/user'
import useCurrentHcs from '../../../hooks/useCurrentHcs'
import { useAppDispatch } from '../../../store'
import useFormUtils from '../../Common/form/_utils/useFormUtils'
import { ActionDialogButton } from '../../feature/actionDialog/_props'
import { UserData } from '../_props'
import { buildFormValuesFromRpps, handleRedirect, rppsDefaultData, userFormConfig } from './_utils'
import { useUserFormContext } from './userFormContext'
import useUserFormGlobalError from './useUserFormGlobalError'

type UseUserFormBaseProps = {
    mode: 'CREATION' | 'EDITION'
    data: UserData
    existingUserLabel?: string
    returnLocation: string
}

const useUserFormBase = ({ mode, data, existingUserLabel = "L'utilisateur", returnLocation }: UseUserFormBaseProps) => {
    const { update, state } = useUserFormContext()
    const editMode = state.isEditionMode
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const currentHcs = useCurrentHcs()
    const { setGlobaleErrorMessage, clearGlobaleErrorMessage, globalErrorMessage } = useUserFormGlobalError()
    const [userRpps, setUserRpps] = React.useState<Partial<UserData> | undefined>(undefined)
    const [searchRppsHasError, setSearchRppsHasError] = React.useState(false)
    const [shouldRedirect, setShouldRedirect] = React.useState(false)

    const certifiedAccessMode = currentHcs.accessMode.toLowerCase() === 'conforme'

    const cancelButton: ActionDialogButton = {
        label: 'Annuler',
        colorVariant: 'light',
        action: () => update({ redirectKey: undefined }),
    }

    const redirectButton: ActionDialogButton = {
        label: 'Poursuivre',
        icon: <ArrowForwardIosIcon />,
        colorVariant: 'dark',
        action: () => {
            if (state.redirectKey) {
                const intialKey = state.redirectKey
                update({ redirectKey: undefined })
                handleRedirect(intialKey)
            }
        },
    }

    const form = useForm<UserData>({
        mode: 'onChange',
        defaultValues: data,
    })

    React.useEffect(() => {
        if (data) {
            form.reset(data)
        }
    }, [data, form])

    const { resetForm } = useFormUtils<UserData>(form)

    const {
        formState: { errors },
        handleSubmit,
        reset,
        trigger,
        watch,
    } = form

    const watched = {
        validRpps: watch('validRpps'),
        rpps: watch('rpps'),
    }

    const handleTriggerFormErrors = React.useCallback(
        (errs: FieldErrors<UserData>) => {
            if (!watched.validRpps) return false
            const errorFields = Object.keys(errs)
            const whitelistErrors = errorFields.filter((x) => userFormConfig.fieldsWhitelists.rpps.find((y) => y === x))

            userFormConfig.fieldsWhitelists.rpps.forEach((field) => {
                const defaultValue = form.watch(field)
                form.resetField(field, {
                    keepError: false,
                    keepDirty: false,
                    keepTouched: false,
                    defaultValue,
                })
                form.clearErrors(field)
            })

            return errorFields.length === whitelistErrors.length
        },
        [form, watched.validRpps],
    )

    const handleTriggerForm = React.useCallback(
        async (shouldResetForm?: boolean) => {
            if (shouldResetForm) reset()
            const success = await trigger()

            const errorHandlingSuccess = handleTriggerFormErrors(form.formState.errors)

            const res = errorHandlingSuccess || success

            if (!res) {
                setGlobaleErrorMessage(
                    'Veuillez corriger tous les champs comportant des erreurs pour pouvoir enregistrer.',
                )
            }

            return res
        },
        [form.formState.errors, handleTriggerFormErrors, reset, trigger],
    )

    React.useEffect(() => {
        if (!state.isEditionMode) {
            update({ isEditionMode: mode === 'EDITION' })
        }
    }, [mode, state.isEditionMode, update])

    React.useEffect(() => {
        const init = async () => {
            if (mode === 'EDITION') {
                await handleTriggerForm(true)
            }
        }

        init()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    React.useEffect(() => {
        update({ shouldBlockNavigation: form.formState.isDirty })
    }, [form.formState.isDirty, update])

    React.useEffect(() => {
        update({ rppsLocked: Boolean(watched.validRpps) })
    }, [update, watched.validRpps])

    React.useEffect(() => {
        if (shouldRedirect) {
            navigate(returnLocation)
        }
    }, [shouldRedirect, navigate, returnLocation])

    const hasFormError = errors !== undefined && Object.keys(errors).length > 0
    const isLoading = state.isSaving || state.isLoading

    const buildHandleSaveTrigger = (handleSave: (savable: UserData) => Promise<void>) => async () => {
        await handleTriggerForm()
        handleSubmit(
            async (savable) => handleSave(savable),
            (errs) => {
                const errorHandlingSuccess = handleTriggerFormErrors(errs)
                if (errorHandlingSuccess) handleSave(watch())
            },
        )()
    }

    const buildHandleSave =
        (save: (savable: UserData) => Promise<boolean | undefined>) => async (savable: UserData) => {
            update({ shouldBlockNavigation: false })
            const res = await save(savable)
            if (res) setShouldRedirect(true)
        }

    const buildHandleCancel = () => () => {
        update({ shouldBlockNavigation: false })
        setShouldRedirect(true)
    }

    const buildSaveButtonAction = (handleSaveTrigger: () => void) => () => {
        if (certifiedAccessMode && !userRpps && !watched.rpps) {
            setSearchRppsHasError(true)
            setGlobaleErrorMessage('Veuillez corriger tous les champs comportant des erreurs pour pouvoir enregistrer.')
            return
        }
        handleSaveTrigger()
    }

    const clearRppsSearch = () => {
        resetForm(rppsDefaultData)
    }

    const handleRppsChange = async (value: string | UserRpps | null) => {
        update({ isLoading: true, loaderMessage: 'Vérification des données...' })

        if (!value || typeof value === 'string') {
            clearRppsSearch()
            update({ isLoading: false, loaderMessage: undefined, annData: undefined })
            return
        }

        const isExistingPhysician = await findPhysicianByRpps(value.identifiantpp.toString())

        if (isExistingPhysician !== false) {
            clearRppsSearch()
            dispatch(displaySnackbar('error', `${existingUserLabel} est déjà existant`))
            update({ isLoading: false })
            return
        }

        const userAnnData = await getIdsAnnDirectoryPhysicianRppsRppsProfessionCodeProfession(
            String(value.identifiantpp),
            String(value.codeprofession),
        )

        console.log('userAnnData from user form', userAnnData)

        update({ loaderMessage: "Chargement des données via l'annuaire santé" })

        const rppsUserValues = buildFormValuesFromRpps({
            userRpps: value,
            annData: userAnnData,
        })

        const professionId = userAnnData.profession?.id ?? ''
        const professionName = userAnnData.profession?.name ?? ''
        const professionPointer = makePointer('Profession', professionId)
        form.setValue('professionPointer', professionPointer)
        form.setValue('profession', professionName)
        form.setValue('professionId', professionId)

        resetForm(rppsUserValues)
        setUserRpps(rppsUserValues)
        update({ isLoading: false, loaderMessage: undefined, annData: userAnnData })
    }

    return {
        form,
        state,
        update,
        editMode,
        watched,
        certifiedAccessMode,
        isLoading,
        hasFormError,
        globalErrorMessage,
        setGlobaleErrorMessage,
        clearGlobaleErrorMessage,
        userRpps,
        searchRppsHasError,
        setSearchRppsHasError,
        handleTriggerFormErrors,
        handleTriggerForm,
        handleSubmit,
        watch,
        handleRppsChange,
        cancelButton,
        redirectButton,
        buildHandleSaveTrigger,
        buildHandleSave,
        buildHandleCancel,
        buildSaveButtonAction,
        setShouldRedirect,
    }
}

export default useUserFormBase
